// The two competitor board calls, built exactly like ../../endpoints.ts (same
// ApiError, same apiUrl, return types from the API's own benchmark contract).
// They live here only until the lead folds them into endpoints.ts; pages keep
// calling `benchmarkApi`, so that move changes one import.
import type { Campaign, BenchmarkBoard } from "@dashboard-intelligent/api/src/modules/benchmark/types";
import type { IndustrySlug } from "@dashboard-intelligent/api/src/modules/industry/types";
import { apiUrl } from "../../client";
import { ApiError } from "../../endpoints";

/** The POST body: a campaign without the fields the server sets (id, recordedOn, isExample). */
export type CampaignInput = Omit<Campaign, "id" | "recordedOn" | "isExample">;

/** One field message from a 400, as the API returns it. */
export interface FieldIssue {
  path: string;
  message: string;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${apiUrl}${path}`, init);
  } catch {
    throw new ApiError("Tidak dapat menghubungi server. Periksa koneksi lalu coba lagi.", 0, null);
  }
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const serverMessage = (body as { error?: unknown } | null)?.error;
    const retry = Number(res.headers.get("Retry-After"));
    throw new ApiError(
      typeof serverMessage === "string" ? serverMessage : `Permintaan gagal (HTTP ${res.status}).`,
      res.status,
      body,
      Number.isFinite(retry) && retry > 0 ? retry : null,
    );
  }
  return body as T;
}

const tenant = (slug: IndustrySlug, rest: string) => `/api/i/${encodeURIComponent(slug)}${rest}`;

export const benchmarkApi = {
  board: (slug: IndustrySlug) => request<BenchmarkBoard>(tenant(slug, "/benchmark")),
  recordCampaign: (slug: IndustrySlug, body: CampaignInput) =>
    request<Campaign>(tenant(slug, "/benchmark/campaign"), {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }),
};

/** The field messages of a 400, or none when the error carries no details. */
export function fieldIssues(error: unknown): FieldIssue[] {
  if (!(error instanceof ApiError) || error.status !== 400) return [];
  const details = (error.body as { details?: unknown } | null)?.details;
  if (!Array.isArray(details)) return [];
  return details.filter(
    (d): d is FieldIssue => typeof d === "object" && d !== null && typeof d.path === "string" && typeof d.message === "string",
  );
}
